import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { SavedLookCard } from "./SavedLookCard";
import { SavedEmptyGuest } from "./SavedEmptyGuest";
import { SavedEmptyAuth } from "./SavedEmptyAuth";

interface SavedScreenProps {
  userId: string | null;
  guestSessionId?: string | null;
  guestHandle?: string | null;
  onViewOutfit: (outfit: SavedOutfitRow) => void;
  onGuestSignup: () => void;
  onGoHome: () => void;
}

export type SavedOutfitRow = {
  id: string;
  image_url: string;
  image_path: string | null;
  image_paths?: string[] | null;
  lookbook_image_url: string | null;
  created_at: string;
  hero_item_name: string | null;
  hero_item_thumbnail: string | null;
  analysis_mode?: string | null;
  occasion?: string | null;
  is_saved?: boolean | null;
  // LB4-RECENTS-SAVED-ROUTING — detect wardrobe_combinator rows at tap
  // time and route them to StyleYourselfResult instead of viewOutfit.
  input_method?: string | null;
};

function SkeletonCard() {
  return (
    <div
      style={{
        background: "#FFFCF8",
        border: "0.5px solid #E8E2D9",
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      <div
        className="l2f-skeleton"
        style={{ width: "100%", aspectRatio: "3 / 4" }}
      />
      <div style={{ padding: "8px 10px 10px" }}>
        <div
          className="l2f-skeleton"
          style={{ width: "70%", height: 11, borderRadius: 4 }}
        />
        <div
          className="l2f-skeleton"
          style={{ width: "50%", height: 10, borderRadius: 4, marginTop: 4 }}
        />
      </div>
    </div>
  );
}

export function SavedScreen({
  userId,
  guestSessionId,
  guestHandle,
  onViewOutfit,
  onGuestSignup,
  onGoHome,
}: SavedScreenProps) {
  const isGuest = !userId;
  const canFetch = !!userId || (isGuest && !!guestSessionId);
  const [rows, setRows] = useState<SavedOutfitRow[]>([]);
  const [loading, setLoading] = useState<boolean>(canFetch);
  const [signed, setSigned] = useState<Record<string, string>>({});
  const issueByIdRef = useRef<Map<string, number>>(new Map());
  const guestViewedFiredRef = useRef(false);
  const reloadCounter = useRef(0);

  const fetchSaved = useCallback(async () => {
    if (!userId && !(isGuest && guestSessionId)) return;
    setLoading(true);

    let out: SavedOutfitRow[] = [];
    if (userId) {
      const { data, error } = await supabase
        .from("outfits")
        .select(
          "id, image_url, image_path, image_paths, lookbook_image_url, created_at, hero_item_name, hero_item_thumbnail, analysis_mode, occasion, is_saved, input_method",
        )
        .eq("user_id", userId)
        .eq("is_saved", true)
        .order("created_at", { ascending: false });
      if (error) {
        console.error("[SavedScreen] fetch failed", error);
        setRows([]);
        setLoading(false);
        return;
      }
      out = ((data ?? []) as unknown) as SavedOutfitRow[];

      // Issue numbers (authed only — guests rarely care about issue numbers,
      // and we'd need a second RPC).
      const { data: allOrdered } = await supabase
        .from("outfits")
        .select("id, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: true });
      const map = new Map<string, number>();
      ((allOrdered ?? []) as Array<{ id: string }>).forEach((o, i) => {
        map.set(o.id, i + 1);
      });
      issueByIdRef.current = map;
    } else if (guestSessionId) {
      const { data, error } = await (supabase as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
      }).rpc("get_guest_saved_outfits", { p_guest_session_id: guestSessionId });
      if (error) {
        console.error("[SavedScreen] guest fetch failed", error);
        setRows([]);
        setLoading(false);
        return;
      }
      out = ((data ?? []) as unknown) as SavedOutfitRow[];
      const map = new Map<string, number>();
      out
        .slice()
        .reverse()
        .forEach((o, i) => map.set(o.id, i + 1));
      issueByIdRef.current = map;
    }

    setRows(out);

    // Resolve signed URLs for thumbnails (works for guest-owned outfits too).
    const entries = await Promise.all(
      out.map(async (r) => {
        if (r.lookbook_image_url) return [r.id, r.lookbook_image_url] as const;
        const path = r.image_path ?? r.image_paths?.[0] ?? null;
        if (!path) return [r.id, r.image_url ?? ""] as const;
        const { data: s } = await supabase.storage
          .from("outfits")
          .createSignedUrl(path, 60 * 60);
        return [r.id, s?.signedUrl ?? r.image_url ?? ""] as const;
      }),
    );
    setSigned(Object.fromEntries(entries.filter(([, u]) => u)));
    setLoading(false);
  }, [userId, isGuest, guestSessionId]);

  useEffect(() => {
    if (!canFetch) {
      setRows([]);
      setLoading(false);
      return;
    }
    void fetchSaved();
  }, [canFetch, fetchSaved]);

  // Realtime: authed only. Guests use refetch-on-focus instead since
  // postgres_changes can't bind through anon RLS reliably.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`saved-outfits-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "outfits",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          reloadCounter.current += 1;
          void fetchSaved();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, fetchSaved]);

  // Refetch-on-focus for guests.
  useEffect(() => {
    if (!isGuest || !guestSessionId) return;
    const handler = () => void fetchSaved();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [isGuest, guestSessionId, fetchSaved]);

  const state: "guest_empty" | "empty" | "has_looks" =
    isGuest && rows.length === 0
      ? "guest_empty"
      : rows.length === 0
        ? "empty"
        : "has_looks";

  useEffect(() => {
    if (loading) return;
    trackEvent("saved_tab_viewed", { count: rows.length, state });
  }, [loading, rows.length, state]);

  // PQ-4: guest_saved_viewed — mount-only, fires once when guest's saved
  // list resolves (regardless of count).
  useEffect(() => {
    if (loading) return;
    if (!isGuest) return;
    if (guestViewedFiredRef.current) return;
    guestViewedFiredRef.current = true;
    trackEvent("guest_saved_viewed", {
      handle: guestHandle ?? null,
      saved_count: rows.length,
      is_empty: rows.length === 0,
    });
  }, [loading, isGuest, guestHandle, rows.length]);

  const handleUnsave = useCallback(
    async (outfitId: string) => {
      const previous = rows;
      const removed = rows.find((r) => r.id === outfitId);
      setRows((prev) => prev.filter((r) => r.id !== outfitId));

      let undone = false;
      const timer = setTimeout(async () => {
        if (undone) return;
        let err: unknown = null;
        if (isGuest && guestSessionId) {
          const { error } = await (supabase as unknown as {
            rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
          }).rpc("unsave_guest_outfit", {
            p_outfit_id: outfitId,
            p_guest_session_id: guestSessionId,
          });
          err = error;
        } else {
          const { error } = await supabase
            .from("outfits")
            .update({ is_saved: false })
            .eq("id", outfitId);
          err = error;
        }
        if (err) {
          console.error("[SavedScreen] unsave failed", err);
          toast.error("Could not remove. Please try again.");
          setRows(previous);
        }
      }, 4000);

      toast("Removed from saved", {
        duration: 4000,
        action: {
          label: "Undo",
          onClick: () => {
            undone = true;
            clearTimeout(timer);
            if (removed) {
              setRows((prev) =>
                [removed, ...prev].sort((a, b) =>
                  b.created_at.localeCompare(a.created_at),
                ),
              );
            }
          },
        },
      });
    },
    [rows, isGuest, guestSessionId],
  );

  const grid = useMemo(() => {
    if (loading) {
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      );
    }
    if (state === "guest_empty") {
      return <SavedEmptyGuest onSignup={onGuestSignup} guestHandle={guestHandle} />;
    }
    if (state === "empty") {
      return <SavedEmptyAuth onGoHome={onGoHome} />;
    }
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
        }}
      >
        {rows.map((r) => (
          <SavedLookCard
            key={r.id}
            outfitId={r.id}
            issueNumber={issueByIdRef.current.get(r.id) ?? 1}
            imageUrl={signed[r.id] ?? r.hero_item_thumbnail ?? null}
            heroName={r.hero_item_name ?? r.occasion ?? "Saved look"}
            heroRetailer={null}
            createdAt={r.created_at}
            onTap={() => onViewOutfit(r)}
            onUnsave={() => void handleUnsave(r.id)}
          />
        ))}
      </div>
    );
  }, [loading, state, rows, signed, onGuestSignup, onGoHome, onViewOutfit, handleUnsave, guestHandle]);

  const showGuestFooter = isGuest && rows.length > 0;

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "#E8E0D5",
        paddingTop: "var(--safe-top, 0px)",
        paddingBottom: "calc(var(--safe-bottom, 0px) + 80px)",
      }}
    >
      <header
        style={{
          padding: "16px 14px 14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div>
          <div
            className="l2f-type-section-header"
            style={{ color: "#C84B2F" }}
          >
            LIKE2FIT
          </div>
          <h1
            className="l2f-type-h1"
            style={{ marginTop: 2, color: "#2C2820" }}
          >
            Saved looks
          </h1>
        </div>
        {state === "has_looks" && (
          <span
            className="l2f-type-caption"
            style={{
              background: "rgba(255,255,255,0.5)",
              border: "1px solid rgba(0,0,0,0.07)",
              borderRadius: 14,
              padding: "3px 10px",
              color: "#4A4540",
            }}
          >
            {rows.length} look{rows.length === 1 ? "" : "s"}
          </span>
        )}
      </header>
      <div style={{ padding: "0 14px" }}>{grid}</div>
      {showGuestFooter && (
        <div
          style={{
            margin: "20px 14px 0",
            padding: "16px 18px",
            borderRadius: 16,
            background: "rgba(200,75,47,0.06)",
            border: "0.5px solid rgba(200,75,47,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: 16,
              color: "#2C2820",
              flex: 1,
            }}
          >
            Sign up to keep these forever.
          </p>
          <button
            type="button"
            onClick={() => {
              trackEvent("guest_saved_signup_tapped", {
                handle: guestHandle ?? null,
                saved_count: rows.length,
              });
              onGuestSignup();
            }}
            style={{
              background: "#C84B2F",
              color: "#fff",
              border: "none",
              borderRadius: 22,
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Sign up
          </button>
        </div>
      )}
    </div>
  );
}
